import React from "react";
import { Mic } from "lucide-react";

// Faithful in-page frames of the real Chapter One UI (prompt text and Mirror reply are live product output).
const PROMPT = "Describe a memory from childhood where you felt truly seen. What made that moment feel safe?";
const SPOKEN =
  "My grandmother let me help her bake bread on Sundays. She'd hand me the dough and just listen to whatever story I was telling, like it mattered as much as the bread.";
const MIRROR =
  "There is something quietly profound about being allowed into someone's ritual — not just tolerated, but genuinely included in the work of their hands. Your grandmother held your stories at the same time she held the dough. What is it about that kitchen you still reach for?";

function Frame({ label, children, testid }) {
  return (
    <figure
      data-testid={testid}
      className="border border-obsidian-600 bg-obsidian-800/70 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.8)]"
    >
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-obsidian-600">
        <span className="h-2 w-2 rounded-full bg-obsidian-600" />
        <span className="h-2 w-2 rounded-full bg-obsidian-600" />
        <span className="ml-3 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500">{label}</span>
      </div>
      <div className="p-5 sm:p-6">{children}</div>
    </figure>
  );
}

export function SpokenEntryShot() {
  return (
    <Frame label="Chapter One · prompt 1 · spoken" testid="product-shot-spoken">
      <p className="font-serif text-lg text-bone-100 leading-snug mb-4">{PROMPT}</p>
      <div className="flex items-center gap-3 mb-4" aria-hidden="true">
        <span className="h-10 w-10 rounded-full bg-terracotta flex items-center justify-center">
          <Mic className="h-4 w-4 text-obsidian-900" />
        </span>
        <span className="flex items-end gap-[3px] h-6">
          {[8, 14, 22, 12, 18, 24, 10, 16, 20, 9, 15, 7].map((h, i) => (
            <span key={i} className="w-[3px] bg-terracotta/70" style={{ height: h }} />
          ))}
        </span>
        <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500">0:41 · transcribed</span>
      </div>
      <p className="font-sans text-sm text-bone-300 leading-relaxed border-l border-terracotta/50 pl-4">{SPOKEN}</p>
    </Frame>
  );
}

const CHAPTERS = ["The Inner Child", "The Core Wound", "The Limiting Belief", "The Shadow Trait", "The Trigger", "The Forgiveness", "The Integration"];

export function ChapterListShot() {
  return (
    <Frame label="The workbook · seven chapters" testid="product-shot-chapters">
      <ol className="space-y-2.5">
        {CHAPTERS.map((c, i) => (
          <li key={c} className="flex items-baseline gap-4">
            <span className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-terracotta w-6">{String(i + 1).padStart(2, "0")}</span>
            <span className={`font-serif text-lg ${i === 0 ? "text-bone-100" : "text-bone-400"}`}>{c}</span>
            {i === 0 && <span className="ml-auto font-sans text-[0.55rem] uppercase tracking-[0.2em] text-bone-500">open · no account</span>}
          </li>
        ))}
      </ol>
    </Frame>
  );
}

export function ChapterMirrorShot() {
  return (
    <Frame label="The Inner Child · the Mirror" testid="product-shot-mirror">
      <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-terracotta mb-3">Mirror · one reflection</p>
      <p className="font-serif italic text-bone-200 leading-relaxed">{MIRROR}</p>
    </Frame>
  );
}
